import { useState } from "react";
import { Alert, Button, Chip, ConfirmDialog, ToggleInput } from "@a2z/ui";
import type { FeatureFlagView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateFeatureFlagMutation } from "../services/useUpdateFeatureFlagMutation";
import { useDeleteFeatureFlagMutation } from "../services/useDeleteFeatureFlagMutation";
import { FeatureFlagAuditPanel } from "./FeatureFlagAuditPanel";

type Props = {
  flag: FeatureFlagView;
  onEdit: (flag: FeatureFlagView) => void;
};

type Pending = { kind: "toggle"; next: boolean } | { kind: "delete" } | null;

export function FeatureFlagRow({ flag, onEdit }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const update = useUpdateFeatureFlagMutation();
  const remove = useDeleteFeatureFlagMutation();
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pending, setPending] = useState<Pending>(null);

  const isPending = update.isPending || remove.isPending;
  const name = (locale === "ar" ? flag.nameAr : flag.nameEn) ?? flag.nameEn;
  const description =
    locale === "ar" ? flag.descriptionAr : flag.descriptionEn;

  async function applyActive(next: boolean): Promise<void> {
    setError(null);
    try {
      await update.mutateAsync({ id: flag.id, isActive: next });
    } catch (err) {
      setError(messageFor(err, t));
    }
  }

  async function performDelete(): Promise<void> {
    setError(null);
    try {
      await remove.mutateAsync(flag.id);
    } catch (err) {
      setError(messageFor(err, t));
    }
  }

  return (
    <li className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)]">
      <div className="px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">
                {name}
              </h3>
              <code className="font-mono text-[11px] text-[color:var(--text-muted)]">
                {flag.key}
              </code>
            </div>
            {description ? (
              <p className="mt-1 text-xs leading-relaxed text-[color:var(--text-muted)]">
                {description}
              </p>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Chip tone={flag.isActive ? "success" : "neutral"} size="sm">
                {flag.isActive
                  ? t(keys.featureFlags.statusActive)
                  : t(keys.featureFlags.statusInactive)}
              </Chip>
              <Chip tone="accent" size="sm">
                {targetingLabel(flag, t)}
              </Chip>
              <span className="ms-1 text-[11px] text-[color:var(--text-muted)]">
                {t(keys.featureFlags.updatedAt, {
                  date: formatTimestamp(flag.updatedAt, locale),
                })}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ToggleInput
              name={`active-${flag.id}`}
              ariaLabel={t(keys.featureFlags.formActiveLabel)}
              checked={flag.isActive}
              disabled={isPending}
              onChange={(event) =>
                setPending({ kind: "toggle", next: event.target.checked })
              }
            />
          </div>
        </div>

        {error ? (
          <div className="mt-3">
            <Alert message={error} />
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="text-xs font-medium text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:underline max-lg:min-h-10"
          >
            {historyOpen
              ? t(keys.featureFlags.hideHistory)
              : t(keys.featureFlags.showHistory)}
          </button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onEdit(flag)}
            disabled={isPending}
            className="h-8 rounded-md px-3 text-xs font-semibold max-lg:h-10"
          >
            {t(keys.featureFlags.editAction)}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setPending({ kind: "delete" })}
            disabled={isPending}
            className="h-8 rounded-md px-3 text-xs font-semibold max-lg:h-10"
          >
            {t(keys.featureFlags.deleteAction)}
          </Button>
        </div>
      </div>

      {historyOpen ? (
        <div className="border-t border-[color:var(--border-light)] px-4 py-3 sm:px-5">
          <FeatureFlagAuditPanel flagId={flag.id} />
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={pending?.kind === "toggle"}
        // Title reflects the state being moved TO, so the dialog reads as the
        // action rather than the current value.
        title={
          pending?.kind === "toggle" && pending.next
            ? t(keys.featureFlags.statusActive)
            : t(keys.featureFlags.statusInactive)
        }
        body={t(keys.featureFlags.formActiveHelper)}
        confirmLabel={t(keys.common.save)}
        cancelLabel={t(keys.common.cancel)}
        // Not destructive — a flag toggle is reversible. "confirm" gives the
        // check glyph + primary button; "danger" would show a trash icon.
        intent="confirm"
        isSubmitting={isPending}
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          if (pending?.kind === "toggle") {
            const next = pending.next;
            setPending(null);
            await applyActive(next);
          }
        }}
      />
      <ConfirmDialog
        isOpen={pending?.kind === "delete"}
        title={t(keys.featureFlags.confirmDeleteTitle)}
        body={t(keys.featureFlags.confirmDeleteBody)}
        confirmLabel={t(keys.featureFlags.confirmDeleteAction)}
        cancelLabel={t(keys.common.cancel)}
        intent="danger"
        isSubmitting={isPending}
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          setPending(null);
          await performDelete();
        }}
      />
    </li>
  );
}

function targetingLabel(
  flag: FeatureFlagView,
  t: ReturnType<typeof useTranslations>,
): string {
  if (flag.enableForAllStores) return t(keys.featureFlags.targetAllStores);
  if (flag.targetStores.length === 0)
    return t(keys.featureFlags.targetNoStores);
  return t(keys.featureFlags.targetStoresCount, {
    count: flag.targetStores.length,
  });
}

function formatTimestamp(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale);
}

function messageFor(
  err: unknown,
  t: ReturnType<typeof useTranslations>,
): string {
  if (err instanceof ApiJsonClientError) {
    return err.message || t(keys.errors.generic);
  }
  return t(keys.errors.generic);
}
